import type { MbscCalendarEvent } from '@akoom-tech/mobiscroll-react';
import {
  addDays,
  differenceInCalendarDays,
  differenceInCalendarWeeks,
  endOfDay,
  format,
  isSameDay,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import type { ProviderTimeOff } from '@/features/users/shared/types';
import { formatDateToISOString } from './bookingDataParser';

const DAY_CODES = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

type BuildSchedulerProviderTimeOffEventsOptions = {
  rangeStart: string;
  rangeEnd: string;
};

const toLondonDate = (value: string | null | undefined) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(
    parsed.toLocaleString('en-US', { timeZone: 'Europe/London' })
  );
};

const dateMax = (left: Date, right: Date) => (left > right ? left : right);
const dateMin = (left: Date, right: Date) => (left < right ? left : right);

const toWeekDays = (value?: string | null) =>
  new Set(
    (value ?? '')
      .split(',')
      .map((day) => day.trim())
      .filter(Boolean)
  );

const resolveRepeatLabel = (
  recurrence: ProviderTimeOff['offday_recurrence']
) => {
  if (!recurrence?.repeat_string) return null;
  if (
    recurrence.repeat_string.toLowerCase() === 'weekly' &&
    recurrence.interval_number === 2
  ) {
    return 'fortnightly';
  }
  return recurrence.repeat_string.toLowerCase();
};

const formatTypeLabel = (value?: string | null) => {
  const normalized = value?.trim();
  if (!normalized) return 'Unavailable';
  return `${normalized.charAt(0).toUpperCase()}${normalized
    .slice(1)
    .toLowerCase()}`;
};

export const resolveProviderTimeOffProviderId = (timeOff: ProviderTimeOff) => {
  const rawId = timeOff.provider?.id ?? timeOff.user_id ?? timeOff.provider_id;
  const parsed = Number(rawId);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const buildTimeOffTitle = (timeOff: ProviderTimeOff) => {
  const repeatLabel = resolveRepeatLabel(timeOff.offday_recurrence);
  const labelParts = [
    formatTypeLabel(
      timeOff.type?.description ?? timeOff.offday_request_types?.description
    ),
    repeatLabel ? `recurring ${repeatLabel}` : null,
    timeOff.notes?.trim().toLowerCase(),
  ].filter(Boolean);

  return labelParts.join(' · ') || 'Unavailable';
};

const buildTimeOffEvent = (
  timeOffId: number,
  start: Date,
  end: Date,
  resourceId: number,
  title: string
): MbscCalendarEvent => ({
  id: `time-off-${timeOffId}-${format(start, 'yyyyMMdd')}`,
  start: formatDateToISOString(start),
  end: formatDateToISOString(end),
  resource: resourceId,
  title,
  editable: false,
  dragInTime: false,
  dragBetweenResources: false,
  resize: false,
  isTimeOff: true,
  cssClass: 'timeoff',
});

export const buildSchedulerProviderTimeOffEvents = (
  providerTimeOff: ProviderTimeOff[] | undefined,
  { rangeEnd, rangeStart }: BuildSchedulerProviderTimeOffEventsOptions
): MbscCalendarEvent[] => {
  if (!providerTimeOff?.length) return [];

  const visibleRangeStart = startOfDay(
    toLondonDate(`${rangeStart}T00:00:00.000Z`) ?? new Date(rangeStart)
  );
  const visibleRangeEnd = endOfDay(
    toLondonDate(`${rangeEnd}T00:00:00.000Z`) ?? new Date(rangeEnd)
  );

  return providerTimeOff
    .flatMap((timeOff) => {
      const startRef = toLondonDate(timeOff.start_date);
      const endRef = toLondonDate(timeOff.end_date);
      const resourceId = resolveProviderTimeOffProviderId(timeOff);
      const recurrence = timeOff.offday_recurrence;

      if (!startRef || !endRef || !resourceId) {
        return [];
      }

      const title = buildTimeOffTitle(timeOff);

      if (!recurrence) {
        const occurrences: MbscCalendarEvent[] = [];
        for (
          let cursor = new Date(visibleRangeStart);
          cursor <= visibleRangeEnd;
          cursor = addDays(cursor, 1)
        ) {
          const dayStart = startOfDay(cursor);
          const dayEnd = endOfDay(cursor);
          const occurrenceStart = dateMax(startRef, dayStart);
          const occurrenceEnd = dateMin(endRef, dayEnd);

          if (occurrenceStart < occurrenceEnd) {
            occurrences.push(
              buildTimeOffEvent(
                timeOff.id,
                occurrenceStart,
                occurrenceEnd,
                resourceId,
                title
              )
            );
          }
        }

        return occurrences;
      }

      const recurrenceStart = toLondonDate(recurrence.from_date) ?? startRef;
      const recurrenceUntil = toLondonDate(recurrence.until ?? null);
      const repeat = recurrence.repeat_string.toLowerCase();
      const interval =
        repeat === 'fortnightly' ? 2 : recurrence.interval_number || 1;
      const weekDays = toWeekDays(recurrence.week_days);

      const occurrences: MbscCalendarEvent[] = [];
      for (
        let cursor = new Date(visibleRangeStart);
        cursor <= visibleRangeEnd;
        cursor = addDays(cursor, 1)
      ) {
        if (cursor < startOfDay(recurrenceStart)) continue;
        if (recurrenceUntil && cursor > endOfDay(recurrenceUntil)) continue;

        if (repeat === 'weekly' || repeat === 'fortnightly') {
          const code = DAY_CODES[cursor.getDay()];
          if (weekDays.size && !weekDays.has(code)) continue;

          const weekDiff = differenceInCalendarWeeks(
            startOfWeek(cursor, { weekStartsOn: 1 }),
            startOfWeek(recurrenceStart, { weekStartsOn: 1 }),
            { weekStartsOn: 1 }
          );
          if (weekDiff % interval !== 0) continue;
        } else if (repeat === 'daily') {
          const dayDiff = differenceInCalendarDays(
            startOfDay(cursor),
            startOfDay(recurrenceStart)
          );
          if (dayDiff % interval !== 0) continue;
        } else if (!isSameDay(cursor, startRef)) {
          continue;
        }

        const occurrenceStart = new Date(cursor);
        occurrenceStart.setHours(
          startRef.getHours(),
          startRef.getMinutes(),
          0,
          0
        );
        const occurrenceEnd = new Date(cursor);
        occurrenceEnd.setHours(endRef.getHours(), endRef.getMinutes(), 0, 0);
        if (occurrenceEnd <= occurrenceStart) {
          occurrenceEnd.setDate(occurrenceEnd.getDate() + 1);
        }

        occurrences.push(
          buildTimeOffEvent(
            timeOff.id,
            occurrenceStart,
            occurrenceEnd,
            resourceId,
            title
          )
        );
      }

      return occurrences;
    })
    .filter((event): event is MbscCalendarEvent => Boolean(event));
};
